import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { isOccupantActive } from '@shared/types/occupants';
import { parseToDayMonthFormat } from '@shared/utils/dateFormatters';
import { deriveTableDisplayName } from '@shared/utils/deriveTableDisplayName';
import { ISOTimeTo12HourTime } from '@shared/utils/time';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { isReservation, type ServiceOccupant } from '../apiHelpers';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';
import { ServiceStatusSelect } from '../serviceStatus/ServiceStatusSelect';
import styles from './OccupantDetailsHeader.scss';

export const OccupantDetailsHeader = ({
  occupant,
  fetchOccupant,
}: {
  occupant: ServiceOccupant;
  fetchOccupant: () => void;
}) => {
  const { closeDrawer, enterReservationEditMode } = useServiceSearchParams();

  const isEditableReservation =
    isReservation(occupant) && isOccupantActive(occupant);

  return (
    <div className={styles.headerBar}>
      <div className={styles.occupantSummary}>
        {isReservation(occupant) && (
          <button
            className={cx(styles.occupantSummaryGroup, styles.tableName)}
            onClick={enterReservationEditMode}
            type="button"
            disabled={!isEditableReservation}
          >
            <div className={styles.summaryItem}>
              <Icon name="table" className={styles.icon} />
              <span className={typography.h7_20}>
                {deriveTableDisplayName({
                  seatedTableName: occupant.seatedTableName,
                  isMultiTable: occupant.isMultiTable,
                  listingName: occupant.listingName,
                })}
              </span>
            </div>
          </button>
        )}
        {!isReservation(occupant) && (
          <div className={cx(styles.occupantSummaryGroup, styles.tableName)}>
            <div className={styles.summaryItem}>
              <Icon name="table" className={styles.icon} />
              <span className={typography.h7_20}>
                {occupant.seatedTableName}
              </span>
            </div>
          </div>
        )}
        <button
          className={styles.occupantSummaryGroup}
          disabled={!isEditableReservation}
          onClick={enterReservationEditMode}
          type="button"
        >
          <div className={styles.summaryItem} data-testid="occupant-date">
            <Icon name="calendar" className={styles.icon} aria-label="Date" />
            <span className={typography.h7_20}>
              {parseToDayMonthFormat(occupant.date)}
            </span>
          </div>
          <div className={styles.summaryItem} data-testid="occupant-time">
            <Icon name="clock" className={styles.icon} aria-label="Time" />
            <span className={typography.h7_20}>
              {ISOTimeTo12HourTime(occupant.time)}
            </span>
          </div>
          <div
            className={styles.summaryItem}
            data-testid="occupant-guest-count"
          >
            <Icon
              name="couple"
              className={styles.icon}
              aria-label="Guest Count"
            />
            <span className={typography.h7_20}>{occupant.guestCount}</span>
          </div>
        </button>
      </div>

      <div className={styles.actions}>
        <ServiceStatusSelect
          occupant={occupant}
          onStatusChange={fetchOccupant}
          showLabel
          value={occupant.serviceStatus}
        />
        <Button
          onClick={closeDrawer}
          label="Close"
          variant={ButtonVariants.Tertiary}
        />
      </div>
    </div>
  );
};
